/**
 * @description
 * 하나투어 토큰 관련 유틸
 * 서버에서만 동작하는 로직이다
 * @author 이영우
 */
import { deployEnv, hosts } from '~/config'
import isEmpty from 'lodash/isEmpty'
import moment from 'moment'

export const ISSUE_TYPE = {
  ANONYMOUS: 'ANONYMOUS',
  LOGIN: 'LOGIN',
  AUTOLOGIN: 'AUTOLOGIN',
  REFRESH: 'REFRESH',
  SERVICE: 'SERVICE',
  RESUME: 'RESUME',
  CONNECT: 'CONNECT',
  STATUS: 'STATUS',
}

const loginApiUrl = `${hosts.hanatourGw}/accounts/auth/login`

const MIN_TOKEN_EXPIRE_TIME = 20

export const refreshHanatourToken = async (context, app, store) => {
  console.log('x-auth-token refresh start==================')

  const headers = context.headers
  const res = context.res

  if (!hasRefreshToken(context)) {
    // 리프레시 토큰이 없다면 로그아웃

    store.dispatch('logout', context)
    await resetToken(app, res, store)

    return
  }

  if (headers['x-auth-token'] && headers['x-auth-refresh-token']) {
    // 웹 페이지에서 호출하는 경우에는 토큰값이 쿠키에 들어있다
    // X-AUTH-REFRESH-TOKEN 은 항상 있어야 한다 (로그인 상태일 때: userNo 가 CNumber, 로그아웃 상태일 때: userNo 가 ANONYMOUS)
    // X-AUTH-TOKEN 은 없을 경우 X-AUTH-REFRESH-TOKEN 을 통하여 재발급 받을 수 있다

    const xAuthToken = headers['x-auth-token']
    const xAuthRefreshToken = headers['x-auth-refresh-token']

    const options = {
      withCredentials: true,
      headers: {
        [xAuthTokenName]: xAuthToken,
        [xAuthRefreshTokenName]: xAuthRefreshToken,
      },
      timeout: 5000,
      host: hosts.hanatourGw.replace('https://', ''),
    }

    let response = {}

    try {
      response = await app.$http.post(
        loginApiUrl,
        {
          issueType: ISSUE_TYPE.STATUS,
        },
        options
      )

      if (!response) {
        throw Error('/accounts/auth/login 응답 데이터가 없습니다')
      }
    } catch (e) {
      try {
        response = await app.$http.post(
          loginApiUrl,
          {
            issueType: ISSUE_TYPE.ANONYMOUS,
          },
          options
        )
      } catch (e) {
        console.error(e)
      }
    } finally {
      if (!isEmpty(response)) {
        res.setHeader('Set-Cookie', response.headers['set-cookie'])
        store.commit('SET_REFRESH_EXPIRED_TIME', response.data.data.expireTime)
      }
    }
  } else {
    // 웹 페이지에서 호출하는 경우에는 토큰값이 쿠키에 들어있다
    // X-AUTH-REFRESH-TOKEN 은 항상 있어야 한다 (로그인 상태일 때: userNo 가 CNumber, 로그아웃 상태일 때: userNo 가 ANONYMOUS)
    // X-AUTH-TOKEN 은 없을 경우 X-AUTH-REFRESH-TOKEN 을 통하여 재발급 받을 수 있다

    const rawCookies = context.rawCookies

    const { xAuthTokenName, xAuthRefreshTokenName } = getAuthTokenCookieName()

    const xAuthRefreshTokenCookie = getCookieByString(xAuthRefreshTokenName, rawCookies)
    const xAuthRefreshToken = parseJWT(xAuthRefreshTokenCookie)

    if (isExpiredToken(xAuthRefreshToken)) {
      // 리프레시 토큰이 만료되었다면 로그아웃
      store.dispatch('logout', context)
      await resetToken(app, res, store)

      return
    }

    const xAuthTokenCookie = getCookieByString(xAuthTokenName, rawCookies)
    let xAuthToken = null

    if (xAuthTokenCookie) {
      // X-AUTH-TOKEN 은 없을 수도 있기 때문에 체크
      xAuthToken = parseJWT(xAuthTokenCookie)
    }

    if (xAuthToken) {
      // X-AUTH-TOKEN 이 있을 때만 아래 로직이 타도록

      if (
        xAuthToken.userNo === ISSUE_TYPE.ANONYMOUS &&
        (context.cookie.auth || context.cookie['hnt_uid'])
      ) {
        // X-AUTH-TOKEN 이 ANONYMOUS 인데
        // auth 나 hnt_uid 가 있다면 로그아웃
        store.dispatch('logout', context)
        await resetToken(app, res, store)

        return
      }

      if (xAuthToken.userNo !== ISSUE_TYPE.ANONYMOUS && !context.cookie['hnt_uid']) {
        // X-AUTH-TOKEN 토큰이 사용자인데
        // hnt_uid 가 없다면 로그아웃
        // auth 는 클라이언트에서 발급 받기 때문에 없을 수도 있어서 그대로 흘러보낸다

        store.dispatch('logout', context)
        await resetToken(app, res, store)

        return
      }
    }

    const issueType = getIssueType(xAuthRefreshToken, xAuthToken)
    const payload = {
      issueType,
    }

    if (issueType === ISSUE_TYPE.AUTOLOGIN) {
      payload.autoLogin = true
    }

    let response = {}

    try {
      response = await app.$http.post(loginApiUrl, payload, {
        withCredentials: true,
        timeout: 5000,
      })
      if (!response) {
        throw Error('/accounts/auth/login 응답 데이터가 없습니다')
      }
    } catch (e1) {
      try {
        response = await app.$http.post(
          loginApiUrl,
          {
            issueType: ISSUE_TYPE.ANONYMOUS,
          },
          { withCredentials: true, timeout: 5000 }
        )
      } catch (e2) {
        e1.response &&
          console.log(
            '[http.token.response ERROR error-1]' + e1.response.status,
            e1.request.path || e1.config.url
          )
        e2.response &&
          console.log(
            '[http.token.response ERROR error-2]' + e2.response.status,
            e2.request.path || e2.config.url
          )
      }
    } finally {
      if (!isEmpty(response)) {
        res.setHeader('Set-Cookie', response.headers['set-cookie'])
        store.commit('SET_REFRESH_EXPIRED_TIME', response.data.data.expireTime)
      }
    }
  }
}

function isExpiredToken(xAuthToken) {
  const diff = moment(xAuthToken.exp * 1000).diff(moment(), 'minutes')
  return diff < 0
}

function getIssueType(xAuthRefreshToken, xAuthToken) {
  if (xAuthRefreshToken.userNo === ISSUE_TYPE.ANONYMOUS) {
    return ISSUE_TYPE.ANONYMOUS
  }

  const refreshDiff = moment(xAuthRefreshToken.exp * 1000).diff(moment(), 'minutes')

  if (refreshDiff < 0) {
    return ISSUE_TYPE.ANONYMOUS
  }

  if (isEmpty(xAuthToken)) {
    if (xAuthRefreshToken.isAutoLogin) {
      return ISSUE_TYPE.AUTOLOGIN
    } else {
      return ISSUE_TYPE.ANONYMOUS
    }
  }

  const accessDiff = moment(xAuthToken.exp * 1000).diff(moment(), 'minutes')

  if (accessDiff < 0) {
    if (xAuthRefreshToken.isAutoLogin) {
      return ISSUE_TYPE.AUTOLOGIN
    } else {
      return ISSUE_TYPE.ANONYMOUS
    }
  } else if (accessDiff < MIN_TOKEN_EXPIRE_TIME) {
    return ISSUE_TYPE.REFRESH
  } else {
    return ISSUE_TYPE.STATUS
  }
}

export function getAuthTokenCookieName() {
  let postfix = ''

  if (['local', 'dev'].includes(deployEnv)) {
    postfix = '-DEV'
  }

  if (deployEnv.startsWith('qa')) {
    postfix = '-QA'
  }

  const xAuthTokenName = `X-AUTH-TOKEN${postfix}`
  const xAuthRefreshTokenName = `X-AUTH-REFRESH-TOKEN${postfix}`

  return {
    xAuthTokenName,
    xAuthRefreshTokenName,
  }
}

function getCookieByString(name, cookie) {
  let value = cookie.match('(^|;) ?' + name + '=([^;]*)(;|$)')
  return value ? value[2] : null
}

export function hasRefreshToken(context) {
  const rawCookies = context.rawCookies
  const headers = context.headers
  const { xAuthRefreshTokenName } = getAuthTokenCookieName()
  let xAuthRefreshToken = rawCookies ? getCookieByString(xAuthRefreshTokenName, rawCookies) : null

  return !!headers['x-auth-refresh-token'] || !!xAuthRefreshToken
}

export async function resetToken(app, res, store) {
  let response = {}

  try {
    response = await app.$http.post(
      loginApiUrl,
      {
        issueType: ISSUE_TYPE.ANONYMOUS,
      },
      { withCredentials: true, timeout: 5000 }
    )

    if (!isEmpty(response)) {
      res.setHeader('Set-Cookie', response.headers['set-cookie'])
      store.commit('SET_REFRESH_EXPIRED_TIME', response.data.data.expireTime)
    }
  } catch (e) {
    console.error(e)
  }
}

function parseJWT(token) {
  const base64Payload = token.split('.')[1]
  const payload = Buffer.from(base64Payload, 'base64').toString()
  return JSON.parse(payload)
}
